import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

// DataTable component that displays data
const DataTable = ({ data, onResetPage }) => {
  const navigate = useNavigate();
  // State for current page, rows per page, and tooltip
  const [currentPage, setCurrentPageState] = useState(1);
  const rowsPerPage = 30;
  const [tooltip, setTooltip] = useState({
    visible: false,
    content: "",
    position: { top: 0, left: 0 },
  });
  // Ref for note column cells to handle tooltips
  const noteRefs = useRef([]);
  // State for sorting configuration
  const [sortConfig, setSortConfig] = useState({
    key: "userid",
    direction: "descending",
  });

  // Navigate to user details page when row is clicked
  const handleRowClick = (userId) => {
    navigate(`/user/${userId}`);
  };

  // Handle column sorting
  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    const newSortConfig = { key, direction };
    setSortConfig(newSortConfig);
    localStorage.setItem("sortConfig", JSON.stringify(newSortConfig));
  };

  // Sort data based on current configuration
  const sortedData = [...data].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "ascending" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "ascending" ? 1 : -1;
    }
    return 0;
  });

  // Calculate pagination values
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  // Handle page navigation
  const goToPage = (page) => {
    console.log({ page });
    if (page >= 1 && page <= totalPages) {
      setCurrentPageState(Number.parseInt(page));
      localStorage.setItem("previousPage", page);
    }
  };

  // Load previous page from localStorage on mount
  useEffect(() => {
    const storedPage = Number.parseInt(localStorage.getItem("previousPage"));

    if (!storedPage) setCurrentPageState(1);
    else {
      setCurrentPageState(storedPage);
    }

    if (onResetPage) onResetPage(1);
  }, [data, onResetPage]);

  // Load sort configuration from localStorage on mount
  useEffect(() => {
    const storedSortConfig = JSON.parse(localStorage.getItem("sortConfig"));
    if (storedSortConfig) {
      setSortConfig(storedSortConfig);
    }
  }, []);

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full divide-y divide-gray-200 table-fixed">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              <th
                onClick={() => handleSort("userid")}
                className="cursor-pointer px-4 py-2 text-gray-600 hover:bg-gray-200 font-semibold w-[5%] select-none"
              >
                <div className="flex flex-col items-center">
                  <span>ID</span>
                  <div className="flex justify-center mt-1">
                    <ArrowUp
                      className={`w-4 h-4 ${
                        sortConfig.key === "userid" &&
                        sortConfig.direction === "ascending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                    <ArrowDown
                      className={`w-4 h-4 ${
                        sortConfig.key === "userid" &&
                        sortConfig.direction === "descending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                  </div>
                </div>
              </th>
              <th
                onClick={() => handleSort("firstname")}
                className="cursor-pointer px-4 py-2 text-gray-600 hover:bg-gray-200 font-semibold w-[15%] select-none"
              >
                <div className="flex flex-col items-center">
                  <span>Name</span>
                  <div className="flex justify-center mt-1">
                    <ArrowUp
                      className={`w-4 h-4 ${
                        sortConfig.key === "firstname" &&
                        sortConfig.direction === "ascending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                    <ArrowDown
                      className={`w-4 h-4 ${
                        sortConfig.key === "firstname" &&
                        sortConfig.direction === "descending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                  </div>
                </div>
              </th>
              <th
                onClick={() => handleSort("email")}
                className="cursor-pointer px-4 py-2 text-gray-600 hover:bg-gray-200 font-semibold w-[20%] select-none"
              >
                <div className="flex flex-col items-center">
                  <span>Email</span>
                  <div className="flex justify-center mt-1">
                    <ArrowUp
                      className={`w-4 h-4 ${
                        sortConfig.key === "email" &&
                        sortConfig.direction === "ascending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                    <ArrowDown
                      className={`w-4 h-4 ${
                        sortConfig.key === "email" &&
                        sortConfig.direction === "descending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                  </div>
                </div>
              </th>
              <th
                onClick={() => handleSort("city")}
                className="cursor-pointer px-4 py-2 text-gray-600 hover:bg-gray-200 font-semibold w-[8%] select-none"
              >
                <div className="flex flex-col items-center">
                  <span>City</span>
                  <div className="flex justify-center mt-1">
                    <ArrowUp
                      className={`w-4 h-4 ${
                        sortConfig.key === "city" &&
                        sortConfig.direction === "ascending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                    <ArrowDown
                      className={`w-4 h-4 ${
                        sortConfig.key === "city" &&
                        sortConfig.direction === "descending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                  </div>
                </div>
              </th>
              <th
                onClick={() => handleSort("note")}
                className="cursor-pointer px-4 py-2 text-gray-600 hover:bg-gray-200 font-semibold w-[15%] select-none"
              >
                <div className="flex flex-col items-center">
                  <span>Note</span>
                  <div className="flex justify-center mt-1">
                    <ArrowUp
                      className={`w-4 h-4 ${
                        sortConfig.key === "note" &&
                        sortConfig.direction === "ascending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                    <ArrowDown
                      className={`w-4 h-4 ${
                        sortConfig.key === "note" &&
                        sortConfig.direction === "descending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                  </div>
                </div>
              </th>
              <th
                onClick={() => handleSort("swoppacketid")}
                className="cursor-pointer px-4 py-2 text-gray-600 hover:bg-gray-200 font-semibold w-[10%] select-none"
              >
                <div className="flex flex-col items-center">
                  <span className="whitespace-nowrap">Swop Packet ID</span>
                  <div className="flex justify-center mt-1">
                    <ArrowUp
                      className={`w-4 h-4 ${
                        sortConfig.key === "swoppacketid" &&
                        sortConfig.direction === "ascending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                    <ArrowDown
                      className={`w-4 h-4 ${
                        sortConfig.key === "swoppacketid" &&
                        sortConfig.direction === "descending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                  </div>
                </div>
              </th>
              <th
                onClick={() => handleSort("familymembers")}
                className="cursor-pointer px-4 py-2 text-gray-600 hover:bg-gray-200 font-semibold w-[23%] select-none"
              >
                <div className="flex flex-col items-center">
                  <span>Family</span>
                  <div className="flex justify-center mt-1">
                    <ArrowUp
                      className={`w-4 h-4 ${
                        sortConfig.key === "familymembers" &&
                        sortConfig.direction === "ascending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                    <ArrowDown
                      className={`w-4 h-4 ${
                        sortConfig.key === "familymembers" &&
                        sortConfig.direction === "descending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                  </div>
                </div>
              </th>
              <th
                onClick={() => handleSort("hearts")}
                className="cursor-pointer px-4 py-2 text-gray-600 hover:bg-gray-200 font-semibold w-[5%] select-none"
              >
                <div className="flex flex-col items-center">
                  <span>Hearts</span>
                  <div className="flex justify-center mt-1">
                    <ArrowUp
                      className={`w-4 h-4 ${
                        sortConfig.key === "hearts" &&
                        sortConfig.direction === "ascending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                    <ArrowDown
                      className={`w-4 h-4 ${
                        sortConfig.key === "hearts" &&
                        sortConfig.direction === "descending"
                          ? "text-blue-500"
                          : "text-gray-400"
                      }`}
                    />
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentData.map((user, index) => (
              <tr
                key={user.userid}
                onClick={() => handleRowClick(user.userid)}
                className="hover:bg-gray-50 cursor-pointer"
              >
                <td className="px-2 py-3  overflow-hidden text-ellipsis whitespace-nowrap text-sm text-gray-900 text-center">
                  {user.userid}
                </td>
                <td className="px-2 py-3  overflow-hidden text-ellipsis whitespace-nowrap text-sm text-gray-900 text-center">
                  {`${user.firstname} ${user.lastname}`}
                </td>
                <td className="px-2 py-3  overflow-hidden text-ellipsis whitespace-nowrap text-sm text-gray-900 text-center">
                  {user.email}
                </td>
                <td className="px-2 py-3  overflow-hidden text-ellipsis whitespace-nowrap text-sm text-gray-900 text-center">
                  {user.city}
                </td>
                <td
                  ref={(el) => (noteRefs.current[index] = el)}
                  className="px-2 py-3 text-sm text-gray-900 max-w-[150px] truncate text-left"
                  onMouseEnter={() => {
                    const rect = noteRefs.current[index].getBoundingClientRect();
                    const tooltipTop = rect.top + window.scrollY;
                    const tooltipLeft = rect.left;

                    setTooltip({
                      visible: true,
                      content: user.note || "-",
                      position: {
                        top: tooltipTop,
                        left: tooltipLeft,
                      },
                    });
                  }}
                  onMouseLeave={() => {
                    setTooltip({
                      visible: false,
                      content: "",
                      position: { top: 0, left: 0 },
                    });
                  }}
                >
                  {user.note || "-"}
                </td>
                <td className="px-2 py-3 text-sm text-gray-900 text-center overflow-hidden text-ellipsis whitespace-nowrap">
                  {user.swoppacketid || "-"}
                </td>
                <td className="px-2 py-3 text-sm text-gray-900  overflow-hidden text-ellipsis whitespace-nowrap text-center">
                  {user.familymembers || "-"}
                </td>
                <td className="px-2 py-3 text-sm text-gray-900 text-center  overflow-hidden text-ellipsis whitespace-nowrap">
                  {user.hearts}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {tooltip.visible && (
        <div
          className="absolute bg-gray-700 text-white p-2 rounded pointer-events-none"
          style={{
            top: tooltip.position.top,
            left: tooltip.position.left,
            transform: "translateX(0)",
          }}
        >
          {tooltip.content}
        </div>
      )}

      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
        <div className="flex items-center">
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
            <span className="font-medium">
              {Math.min(endIndex, data.length)}
            </span>{" "}
            of <span className="font-medium">{data.length}</span> results
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronsLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <span className="px-4 py-2 text-sm text-gray-700 bg-gray-50 border rounded select-none">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages}
            className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronsRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
